<script setup lang="ts">
import { useToastStore, type ToastVariant } from '@/stores/toast'
import Icon from '@/components/icons/Icon.vue'

const toast = useToastStore()

const icons: Record<ToastVariant, 'check' | 'exclamation' | 'info'> = {
  success: 'check',
  error: 'exclamation',
  info: 'info',
}
</script>

<template>
  <div class="toast-container" aria-live="polite" role="status">
    <TransitionGroup name="toast">
      <div v-for="t in toast.toasts" :key="t.id" class="toast" :class="`toast--${t.variant}`">
        <Icon :name="icons[t.variant]" :size="16" />
        <span class="toast__message">{{ t.message }}</span>
        <button
          type="button"
          class="toast__dismiss"
          aria-label="Dismiss"
          @click="toast.dismiss(t.id)"
        >
          ×
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-container {
  position: fixed;
  bottom: var(--space-6);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background-color: var(--color-surface-raised);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  font-size: var(--text-sm);
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
}

.toast--success {
  border-color: var(--color-success);
  color: var(--color-success);
}

.toast--error {
  border-color: var(--color-error);
  color: var(--color-error);
}

.toast--info {
  border-color: var(--color-info);
  color: var(--color-info);
}

.toast__message {
  color: var(--color-text);
}

.toast__dismiss {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: var(--text-base);
  line-height: 1;
  padding: 0;
}

.toast__dismiss:hover {
  color: var(--color-text);
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
